import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { backendService } from "../services/backendService";
import { useGameState } from "../providers/GameStateProvider";
import { useSupabase } from "../providers/SupabaseProvider";
import { launchFishTokenEffect } from "../game/systems/FishTokenEffect";
import StarRating from "./StarRating";
import { FaFish } from "react-icons/fa6";
import { Level } from "../data/LevelModel";

interface CongratsModalProps {
  open: boolean;
  onClose: () => void;
  score: number;
  tokensAwarded: number;
  moves: number;
  duration: number;
  mistakes: string[];
}

const CongratsModal: React.FC<CongratsModalProps> = ({
  open,
  onClose,
  score,
  tokensAwarded,
  moves,
  duration,
  mistakes,
}) => {
  const { user } = useSupabase();
  const { currentLevel, setCurrentLevel } = useGameState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const closeAndNotify = () => {
    onClose();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("modalClosed"));
    }
  };

  const handleNextLevel = async () => {
    if (!user || !currentLevel) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch the next level from backend
      const nextLevel = await backendService.fetchNextLevel(user.id);

      // Update the game state
      setCurrentLevel(nextLevel);

      // Animate fish coins
      if (buttonRef.current) {
        launchFishTokenEffect(
          buttonRef.current.getBoundingClientRect(),
          tokensAwarded,
        );
      }

      // Close the modal
      closeAndNotify();
    } catch (err) {
      console.error("Failed to fetch next level:", err);
      setError("Failed to load next level. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRetryLevel = async () => {
    if (!user || !currentLevel) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch the same level again from backend
      const retryLevel = await backendService.fetchLevelByName(
        user.id,
        currentLevel.nickname,
      );

      // Close the modal first
      closeAndNotify();

      // Update the game state with the retry level
      setCurrentLevel(retryLevel);

      // Dispatch a custom event to force scene restart
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("forceSceneRestart", {
            detail: { categories: retryLevel.categories },
          }),
        );
      }, 100);
    } catch (err) {
      console.error("Failed to fetch retry level:", err);
      setError("Failed to load retry level. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    closeAndNotify();
  };

  const formatDuration = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1a1a",
          color: "white",
          border: "2px solid #4caf50",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", color: "#4caf50" }}>
        <Typography variant="h4" component="div" sx={{ fontWeight: "bold" }}>
          🎉 Level Complete! 🎉
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ textAlign: "center", py: 2 }}>
          {/* Star Rating */}
          <StarRating mistakes={mistakes} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              mb: 3,
              p: 2,
              bgcolor: "#2a2a2a",
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Typography variant="body2" color="#aaa">
                Moves
              </Typography>
              <Typography variant="h6" color="#64b5f6">
                {moves}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Typography variant="body2" color="#aaa">
                Time
              </Typography>
              <Typography variant="h6" color="#ba68c8">
                {formatDuration(duration)}
              </Typography>
            </Box>
          </Box>

          {/* Tuna Tokens - moved to bottom and made bigger */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              mb: 2,
              p: 2,
              bgcolor: "#2a2a2a",
              borderRadius: 2,
            }}
          >
            <Typography variant="body1" color="#aaa">
              You earned
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h4" color="#ffb74d">
                {tokensAwarded} / 50
              </Typography>
              <FaFish color="#ffb74d" size={32} />
            </Box>
            <Typography variant="body1" color="#aaa">
              tuna tokens
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 2 }}>
        <Button
          onClick={handleRetryLevel}
          variant="outlined"
          disabled={loading}
          sx={{
            borderColor: "#4caf50",
            color: "#4caf50",
            minWidth: 140,
            "&:hover": {
              borderColor: "#45a049",
              bgcolor: "rgba(76, 175, 80, 0.1)",
            },
            "&:disabled": {
              borderColor: "#666",
              color: "#666",
            },
          }}
          style={{ display: "none" }} // TODO: re-enable this
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: "inherit" }} />
              Loading...
            </>
          ) : (
            "Retry Level"
          )}
        </Button>
        <Button
          ref={buttonRef}
          onClick={handleNextLevel}
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: "#4caf50",
            color: "white",
            minWidth: 140,
            "&:hover": {
              bgcolor: "#45a049",
            },
            "&:disabled": {
              bgcolor: "#666",
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
              Loading...
            </>
          ) : (
            "Next Level →"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CongratsModal;
